/**
 * Unified Command & Action Search
 *
 * Merges page actions and overlay commands into a single searchable,
 * sortable result set for the unified CommandPalette.
 *
 * Both page actions and overlay commands are treated uniformly:
 * - scored by relevance against query
 * - grouped by category/group
 * - filtered by availability/context
 * - ranked by priority
 *
 * Phase 6: One command palette exposes all page/entity/workflow/agent actions.
 */

import type { PageAction, PageActionContext } from '../../types/page-actions';
import type { CommandDefinition as OverlayCommand } from '../../ui/commands/command-registry';

// ─── Unified Result Type ───────────────────────────────────────────

/**
 * A unified command result that wraps either a page action or overlay command.
 * The palette renders them identically from this wrapper.
 */
export interface UnifiedCommand {
  /** Unique ID (page action ID or overlay command ID) */
  id: string;
  /** Label shown to user */
  label: string;
  /** Optional short description */
  description?: string;
  /** Optional icon (emoji or lucide name) */
  icon?: string;
  /** Optional keyboard shortcut */
  shortcut?: string;
  /** Group/category for visual grouping */
  group?: string;
  /** Whether this is destructive */
  destructive?: boolean;
  /** Whether this is disabled */
  disabled?: boolean;
  /** Whether action is currently executing */
  executing?: boolean;
  /** Execute the command */
  execute: (context?: PageActionContext) => Promise<void>;
  /** Source: 'page-action' or 'overlay-command' */
  source: 'page-action' | 'overlay-command';
  /** Relevance score (0-1000) */
  score: number;
}

// ─── Conversion Helpers ────────────────────────────────────────────

/**
 * Convert a page action to a unified command.
 */
export function pageActionToUnified(
  action: PageAction,
  context: PageActionContext,
  score: number
): UnifiedCommand {
  return {
    id: action.id,
    label: action.label,
    description: action.description,
    icon: action.icon,
    shortcut: action.shortcut,
    group: action.group ?? action.pluginId,
    destructive: action.destructive,
    disabled: action.disabled,
    score,
    execute: async () => {
      if (!action.disabled) {
        await action.onExecute(context);
      }
    },
    source: 'page-action',
  };
}

/**
 * Convert an overlay command to a unified command.
 */
export function overlayCommandToUnified(
  command: OverlayCommand,
  context: any,
  score: number
): UnifiedCommand {
  return {
    id: command.id,
    label: command.title,
    description: command.description,
    icon: command.icon ? String(command.icon) : undefined,
    group: 'Core',
    disabled: false,
    score,
    execute: async () => {
      try {
        const result = await command.execute(context);
        if (result.kind === 'navigate') {
          window.location.pathname = result.to;
        } else if (result.kind === 'executed-and-navigate') {
          window.location.pathname = result.to;
        }
        // 'executed' — no navigation
      } catch (err) {
        console.error(`[UnifiedCommand] Overlay command "${command.id}" failed:`, err);
      }
    },
    source: 'overlay-command',
  };
}

// ─── Scoring & Filtering ───────────────────────────────────────────

/**
 * Score a unified command (from either source) against a query.
 * Returns 0 for no match, higher for better matches.
 */
export function scoreUnifiedCommand(cmd: UnifiedCommand, query: string): number {
  if (!query) return 50; // base score when no query
  const q = query.toLowerCase();
  let score = 0;

  // Title match (highest weight)
  const titleLower = cmd.label.toLowerCase();
  if (titleLower === q) {
    score += 200;
  } else if (titleLower.startsWith(q)) {
    score += 150;
  } else if (titleLower.includes(q)) {
    score += 100;
  }

  // Description match
  if (cmd.description) {
    const descLower = cmd.description.toLowerCase();
    if (descLower.includes(q)) {
      score += 30;
    }
  }

  // Group match
  if (cmd.group) {
    const groupLower = cmd.group.toLowerCase();
    if (groupLower.includes(q)) {
      score += 20;
    }
  }

  // ID match (power users)
  if (cmd.id.toLowerCase().includes(q)) {
    score += 25;
  }

  return score;
}

/**
 * Filter and score a list of unified commands by query and availability.
 */
export function filterUnifiedCommands(
  commands: UnifiedCommand[],
  query: string
): UnifiedCommand[] {
  // Score all commands
  const scored = commands
    .map((cmd) => ({
      ...cmd,
      score: scoreUnifiedCommand(cmd, query),
    }))
    .filter((cmd) => cmd.score > 0 && !cmd.disabled)
    .sort((a, b) => b.score - a.score);

  return scored;
}

// ─── Grouping ──────────────────────────────────────────────────────

export interface UnifiedGroup {
  key: string;
  label: string;
  commands: UnifiedCommand[];
}

/**
 * Group unified commands by group/category.
 */
export function groupUnifiedCommands(commands: UnifiedCommand[]): UnifiedGroup[] {
  const groups = new Map<string, UnifiedCommand[]>();

  for (const cmd of commands) {
    const key = cmd.group ?? 'General';
    const existing = groups.get(key) ?? [];
    existing.push(cmd);
    groups.set(key, existing);
  }

  return Array.from(groups.entries()).map(([key, cmds]) => ({
    key,
    label: key,
    commands: cmds,
  }));
}
